import { useCallback, useMemo } from "react";
import { useTranslate } from "@growtherapy/i18n";
import { MeasureSchemaAndAnswers } from "../../components/measures/types";
import { PageComponentProps } from "../../components";
import {
  SubmitMeasureResponseCallbackArgs as BaseSubmitMeasureResponseCallbackArgs,
  useSubmitMeasureResponseCallback as useBaseSubmitMeasureResponseCallback,
  MeasureValidationError,
} from "@grow-therapy-team/seedling-components";
import { logger } from "../../datadog";
import { CallbackFailure, CallbackSuccess } from "../../types";
import { toast } from "../../toasts";
import { GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID } from "../../provider/useGetMeasuresByPatientShortId";
type SubmitMeasureResponseResult =
  | CallbackSuccess<string, "SUBMITTED">
  | CallbackFailure<Error, "FAILED_TO_SUBMIT" | "MISSING_REQUIRED_DATA">;

type SubmitMeasureResponseCallbackArgs = Pick<
  PageComponentProps<MeasureSchemaAndAnswers>,
  "page"
> &
  Partial<
    Pick<
      BaseSubmitMeasureResponseCallbackArgs,
      "clientUserShortId" | "providerShortId"
    >
  > &
  Pick<BaseSubmitMeasureResponseCallbackArgs, "completionCampaign">;

type SubmitMeasureResponseCallback = (
  args: SubmitMeasureResponseCallbackArgs,
) => Promise<SubmitMeasureResponseResult>;

export function useSubmitMeasureResponseCallback(): [
  SubmitMeasureResponseCallback,
  { isLoading: boolean },
] {
  const t = useTranslate();
  const startedAt = useMemo(() => new Date().toISOString(), []);

  const [submitMeasureResponse, requestData] =
    useBaseSubmitMeasureResponseCallback();

  const handleSubmission = useCallback(
    async ({
      page,
      clientUserShortId,
      providerShortId,
      completionCampaign,
    }: SubmitMeasureResponseCallbackArgs): Promise<SubmitMeasureResponseResult> => {
      const { schema } = page;
      if (
        !clientUserShortId ||
        !schema ||
        (schema.isProviderSpecific && !providerShortId)
      ) {
        return {
          status: "failure",
          code: "MISSING_REQUIRED_DATA",
          value: Error("Missing required data"),
        };
      }

      try {
        const { data } = await submitMeasureResponse({
          clientUserShortId,
          providerShortId,
          completionCampaign,
          startedAt,
          schema,
          shouldShowErrorToast: false,
          shouldResetMeasureValuesOnCompleted: false,
          apolloOptions: {
            context: {
              skipAuth: true,
            },
            refetchQueries: [GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID],
          },
        });
        return {
          status: "success",
          code: "SUBMITTED",
          value:
            data?.submitMeasureResponse?.measureResponse
              ?.measureResponseShortId ?? "",
        };
      } catch (err) {
        if (!(err instanceof MeasureValidationError)) {
          logger.error(
            "Could not submit measure",
            { clientUserShortId },
            err as Error,
          );
          toast.error(
            t("telehealth-core:forms.unableToSubmitFormDueToAnUnexpectedError"),
          );
        }
        return {
          status: "failure",
          code: "FAILED_TO_SUBMIT",
          value: err as Error,
        };
      }
    },
    [startedAt, submitMeasureResponse, t],
  );

  return [handleSubmission, requestData];
}
